import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Rss } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteCard } from './NoteCard';
import { NoteComposer } from './NoteComposer';
import { SITE } from '@/lib/site';

function FeedSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-5 sm:p-6">
            <div className="mb-3 flex items-center gap-3">
              <Skeleton className="size-10 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function NostrFeed() {
  const { nostr } = useNostr();

  const { data: notes, isLoading, isError } = useQuery({
    queryKey: ['owner-notes', SITE.ownerPubkey],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [1], authors: [SITE.ownerPubkey], limit: 30 }],
        { signal: c.signal },
      );

      // Top-level notes only (replies carry `e` tags), newest first.
      return events
        .filter((event) => !event.tags.some(([name]) => name === 'e'))
        .sort((a, b) => b.created_at - a.created_at)
        .slice(0, 20);
    },
  });

  return (
    <section id="notes" className="scroll-mt-20 border-t border-border/60">
      <div className="container py-20 md:py-28">
        <div className="mx-auto max-w-2xl">
          <div className="mb-12 space-y-4">
            <p className="font-mono text-sm text-bitcoin">// notes from the relay</p>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                What I've been up to
              </h2>
              <a
                href={SITE.nostrProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-bitcoin focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
              >
                View all on Nostr
                <ArrowUpRight className="size-4" />
              </a>
            </div>
            <p className="text-lg text-muted-foreground">
              These posts load straight from public Nostr relays — no platform, no algorithm. Just my notes, signed and timestamped.
            </p>
          </div>

          <div className="space-y-6">
            <NoteComposer />

            {isLoading ? (
              <FeedSkeleton />
            ) : isError ? (
              <Card className="border-dashed">
                <CardContent className="py-12 px-8 text-center">
                  <Rss className="mx-auto mb-4 size-8 text-muted-foreground" />
                  <p className="mx-auto max-w-sm text-muted-foreground">
                    Couldn't reach the relays right now. Try refreshing in a moment.
                  </p>
                </CardContent>
              </Card>
            ) : notes && notes.length > 0 ? (
              <div className="space-y-4">
                {notes.map((note) => (
                  <NoteCard key={note.id} event={note} />
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-12 px-8 text-center">
                  <Rss className="mx-auto mb-4 size-8 text-muted-foreground" />
                  <p className="mx-auto max-w-sm text-muted-foreground">
                    No notes found yet. Check your relay connections, or check back soon.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
